'use client';

import { useLocale } from 'next-intl';
import { ChangelogHashTag } from './changelog-hashtag';
import type { IChangelog } from './interface';

interface IChangelogEntryProps {
  item: IChangelog;
}

export const ChangelogEntry = ({ item }: IChangelogEntryProps): React.JSX.Element => {
  const locale = useLocale();
  return (
    <div className="relative ps-8 border-s-2 border-blue-200 before:absolute before:start-0 before:top-0 before:w-4 before:h-4 before:rounded-full before:bg-blue-600 before:-translate-x-1/2">
      <time className="text-sm text-muted-foreground block mb-2">
        {new Date(item.date).toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </time>
      <h2 className="text-2xl font-semibold mb-3 text-foreground">{item.title}</h2>
      <p className="text-muted-foreground mb-2">
        {item.description?.split(/(#\w+)/g).map((part) => {
          if (part.startsWith('#')) {
            const tag = part.slice(1);
            return (
              <ChangelogHashTag key={part} tag={tag}>
                {part}
              </ChangelogHashTag>
            );
          }
          return part;
        })}
        {item.tags && (
          <span className="inline-flex flex-wrap gap-2 mx-2">
            {item.tags.map((tag) => (
              <ChangelogHashTag key={tag} tag={tag}>
                #{tag}
              </ChangelogHashTag>
            ))}
          </span>
        )}
      </p>
      {item.links && item.links.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {item.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{link.label}</span>
              <svg
                aria-hidden="true"
                className="w-4 h-4 ms-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
